import { useState, useCallback } from 'react';
import { FileText, Upload, Loader2 } from 'lucide-react';
import type { MHRDocument, UploadFile, DocumentCategory, CostEstimate } from '../../types';
import { DropZone, BatchUploadList, AsyncProcessingStatus, CostEstimateModal } from '../Upload';
import { DocumentCard } from './DocumentCard';
import { DocumentSearchBar } from './DocumentSearchBar';
import { DocumentFilterBar } from './DocumentFilterBar';

interface MedicalRecordViewProps {
  documents: MHRDocument[];
  patientId: string;
  onDocumentsChange: () => void;
  uploadFiles: UploadFile[];
  uploading: boolean;
  onFilesSelected: (files: File[]) => void;
  onRemoveFile: (fileId: string) => void;
  onRetryUpload: (fileId: string) => void;
  onUploadAll: () => void;
  asyncProcessingDocs: Set<string>;
  onGetCostEstimate: (docId: string) => void;
  onAsyncProcessingComplete: (docId: string) => void;
  onReprocessDocument: (docId: string) => void;
  reprocessingDocId: string | null;
  onViewDocument: (docId: string, page?: number) => void;
}

export function MedicalRecordView({
  documents,
  patientId,
  onDocumentsChange,
  uploadFiles,
  uploading,
  onFilesSelected,
  onRemoveFile,
  onRetryUpload,
  onUploadAll,
  asyncProcessingDocs,
  onGetCostEstimate,
  onAsyncProcessingComplete,
  onReprocessDocument,
  reprocessingDocId,
  onViewDocument,
}: MedicalRecordViewProps) {
  const [selectedCategory, setSelectedCategory] = useState<DocumentCategory | null>(null);
  const [sortField, setSortField] = useState('uploaded_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Client-side filtering (server-side filtering is also available via API)
  const filteredDocuments = documents.filter(doc => {
    if (selectedCategory) {
      const docCat = doc.document_category || doc.ai_document_category;
      if (docCat !== selectedCategory) return false;
    }
    return true;
  });

  // Client-side sorting
  const sortedDocuments = [...filteredDocuments].sort((a, b) => {
    let aVal: string | number = '';
    let bVal: string | number = '';

    switch (sortField) {
      case 'uploaded_at':
        aVal = a.uploaded_at;
        bVal = b.uploaded_at;
        break;
      case 'document_date':
        aVal = a.document_date || a.ai_document_date || '';
        bVal = b.document_date || b.ai_document_date || '';
        break;
      case 'filename':
        aVal = a.filename.toLowerCase();
        bVal = b.filename.toLowerCase();
        break;
      case 'document_category':
        aVal = a.document_category || a.ai_document_category || 'zzz';
        bVal = b.document_category || b.ai_document_category || 'zzz';
        break;
    }

    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const handleDocumentUpdated = useCallback((updated: MHRDocument) => {
    onDocumentsChange();
  }, [onDocumentsChange]);

  const handleSearchResultClick = useCallback((docId: string, page?: number) => {
    onViewDocument(docId, page);
  }, [onViewDocument]);

  return (
    <div className="space-y-4">
      {/* Search + Filter bar */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        <div className="flex items-center gap-3">
          <DocumentSearchBar
            patientId={patientId}
            onResultClick={handleSearchResultClick}
          />
        </div>
        <DocumentFilterBar
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
          sortField={sortField}
          sortOrder={sortOrder}
          onSortChange={(field, order) => { setSortField(field); setSortOrder(order); }}
        />
      </div>

      {/* Upload zone */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <DropZone onFilesSelected={onFilesSelected} disabled={uploading} />
        {uploadFiles.length > 0 && (
          <div className="mt-4 space-y-4">
            <BatchUploadList files={uploadFiles} onRemove={onRemoveFile} onRetry={onRetryUpload} />
            {uploadFiles.some(f => f.status === 'pending') && (
              <div className="flex justify-end">
                <button
                  onClick={onUploadAll}
                  disabled={uploading}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors min-h-[44px] sm:min-h-0"
                >
                  {uploading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload size={18} />
                      Upload All
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Document count summary */}
      {documents.length > 0 && (
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 px-1">
          <span>
            {sortedDocuments.length === documents.length
              ? `${documents.length} document${documents.length !== 1 ? 's' : ''}`
              : `${sortedDocuments.length} of ${documents.length} documents`}
          </span>
          {documents.filter(d => d.is_duplicate).length > 0 && (
            <span className="text-orange-500 dark:text-orange-400">
              ({documents.filter(d => d.is_duplicate).length} duplicate{documents.filter(d => d.is_duplicate).length !== 1 ? 's' : ''})
            </span>
          )}
        </div>
      )}

      {/* Document cards */}
      {sortedDocuments.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <FileText className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-sm">
            {documents.length === 0
              ? 'No documents yet. Drag and drop files above to upload.'
              : 'No documents match the current filters.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {sortedDocuments.map(doc => (
            <DocumentCard
              key={doc.id}
              document={doc}
              asyncProcessing={asyncProcessingDocs.has(doc.id)}
              reprocessingDocId={reprocessingDocId}
              onViewDocument={onViewDocument}
              onGetCostEstimate={onGetCostEstimate}
              onAsyncProcessingComplete={onAsyncProcessingComplete}
              onReprocessDocument={onReprocessDocument}
              onDocumentUpdated={handleDocumentUpdated}
            />
          ))}
        </div>
      )}
    </div>
  );
}
